import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, CheckCircle2, XCircle, AlertTriangle, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLOR = {
  'Auto Approved':         'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Approved':              'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Requires PM Approval':  'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
  'Pending Estimate':      'bg-blue-500/15 text-blue-300 border-blue-500/30',
  'Rejected':              'bg-red-500/15 text-red-300 border-red-500/30',
};

const WoAuthorizationPanel = ({ workOrderId, onChange }) => {
  const { user } = useAuth();
  const canDecide = ['property_manager', 'asset_manager', 'maintenance_supervisor'].includes(user?.role);
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // 'approve' | 'reject'
  const [note, setNote] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pm/work-orders/${workOrderId}/authorization`, { withCredentials: true });
      setAuth(data);
    } catch (e) {
      // Non-PM users get 403 — just hide the panel
      setAuth(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pm/work-orders/${workOrderId}/authorization`, { withCredentials: true });
        if (alive) setAuth(data);
      } catch { /* hide */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [workOrderId]);

  const submit = async () => {
    setBusy(true);
    try {
      if (dlg === 'approve') {
        const body = { note };
        if (approvedAmount) body.approved_amount = Number(approvedAmount);
        await axios.post(`${API}/pm/work-orders/${workOrderId}/authorize`, body, { withCredentials: true });
        toast.success('Authorized');
      } else {
        await axios.post(`${API}/pm/work-orders/${workOrderId}/reject`, { note, reason }, { withCredentials: true });
        toast.success('Rejected');
      }
      setDlg(null);
      setNote(''); setReason(''); setApprovedAmount('');
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Action failed');
    }
    setBusy(false);
  };

  if (loading) return null;
  if (!auth) return null;

  const statusCls = STATUS_COLOR[auth.status] || 'bg-[#262626] text-[#A3A3A3] border-[#404040]';
  const showActions = canDecide && auth.status === 'Requires PM Approval';

  return (
    <div data-testid="wo-authorization-panel" className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> AUTHORIZATION</p>
        <Badge className={`border ${statusCls}`} data-testid="auth-status">{auth.status}</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-[#A3A3A3] text-xs">Estimated</p>
          <p className="text-white font-medium" data-testid="auth-estimate">{auth.estimated_amount != null ? `$${Number(auth.estimated_amount).toLocaleString()}` : <span className="text-[#525252]">—</span>}</p>
        </div>
        <div>
          <p className="text-[#A3A3A3] text-xs">Auto-approve up to</p>
          <p className="text-white font-medium" data-testid="auth-limit">${Number(auth.effective_limit || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[#A3A3A3] text-xs">Company default</p>
          <p className="text-white">${Number(auth.company_limit || 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[#A3A3A3] text-xs">Property override</p>
          <p className="text-white">{auth.property_limit != null ? `$${Number(auth.property_limit).toLocaleString()}` : <span className="text-[#525252]">none</span>}</p>
        </div>
      </div>

      {auth.decision && (
        <div className="border-t border-[#262626] pt-3 text-xs text-[#A3A3A3]" data-testid="auth-decision">
          <p><span className="text-white font-medium">{auth.decision.by_name}</span> {auth.decision.action === 'approve' ? 'approved' : 'rejected'} this WO on {new Date(auth.decision.at).toLocaleString()}</p>
          {auth.decision.approved_amount && <p>Approved up to <span className="text-white">${Number(auth.decision.approved_amount).toLocaleString()}</span></p>}
          {auth.decision.reason && <p>Reason: <span className="text-white italic">&ldquo;{auth.decision.reason}&rdquo;</span></p>}
          {auth.decision.note && <p>Note: <span className="text-white italic">&ldquo;{auth.decision.note}&rdquo;</span></p>}
        </div>
      )}

      {showActions && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#262626]">
          <p className="text-yellow-300 text-xs flex items-center gap-1 mr-auto"><AlertTriangle className="w-3 h-3" /> Estimate exceeds your auto-approval limit. Your call.</p>
          <Button data-testid="reject-wo-btn" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10" onClick={() => setDlg('reject')}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
          <Button data-testid="approve-wo-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setDlg('approve')}><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{dlg === 'approve' ? 'Approve work order' : 'Reject work order'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              {dlg === 'approve'
                ? 'Approve this repair. Optionally cap the approved amount.'
                : 'Reject this repair. Reason will be sent to NexFlow + the requesting tenant.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dlg === 'approve' && (
              <div>
                <Label>Approved up to <span className="text-[#525252] text-xs">(optional)</span></Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-2.5 w-4 h-4 text-[#525252]" />
                  <Input data-testid="approved-amount" type="number" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} className="pl-7 bg-[#0A0A0A] border-[#262626] text-white" placeholder={auth.estimated_amount?.toString() || ''} />
                </div>
              </div>
            )}
            {dlg === 'reject' && (
              <div>
                <Label>Reason</Label>
                <Input data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="e.g. Out of budget this quarter" />
              </div>
            )}
            <div>
              <Label>Note</Label>
              <Textarea data-testid="decision-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setDlg(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="confirm-decision" className={dlg === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} onClick={submit} disabled={busy}>
              {dlg === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WoAuthorizationPanel;
